import { OperationalStatus } from "./helpers/ropCard3003LiveSingleLineDiagram.types";
import type { SolarGridControlTelemetryData } from "./types";

type Position = { x: number; y: number };

export class PositionAndSizeCalculator {
  private UgInversorsCount: Record<number, number> = {};
  private readonly UG_GAP = 24;
  private readonly INV_COLUMN_STEP = 60;
  private readonly MIN_UG_BORDER_WIDTH = 92;

  public SetInversorsCount(ugInversorsCount: Record<number, number> = {}) {
    this.UgInversorsCount = ugInversorsCount;
  }

  private getUgInversorsAmount(ugIndex: number): number {
    return this.UgInversorsCount[ugIndex + 1] || 0;
  }

  private getPrevInversorsAmount(ugIndex: number): number {
    let prevInversorsAmount = 0;

    for (let i = 1; i < ugIndex + 1; i++) {
      prevInversorsAmount += this.UgInversorsCount[i] || 0;
    }

    return prevInversorsAmount;
  }

  private getUgStartX(ugIndex: number, totalInversors: number): number {
    return ugIndex * this.UG_GAP + totalInversors * this.INV_COLUMN_STEP;
  }

  private getUgCenterX(ugIndex: number, totalInversors: number): number {
    return (
      this.getUgStartX(ugIndex, totalInversors) +
      (this.getUgInversorsAmount(ugIndex) * this.INV_COLUMN_STEP) / 2
    );
  }

  private getDiagramCenterX(): number {
    const ugCount = Object.keys(this.UgInversorsCount).length;

    if (ugCount === 0) {
      return 0;
    }

    let totalInversors = 0;
    let firstCenter = 0;
    let lastCenter = 0;

    for (let ugIndex = 0; ugIndex < ugCount; ugIndex++) {
      const center = this.getUgCenterX(ugIndex, totalInversors);

      if (ugIndex === 0) {
        firstCenter = center;
      }

      lastCenter = center;
      totalInversors += this.getUgInversorsAmount(ugIndex);
    }

    return (firstCenter + lastCenter) / 2;
  }

  getUgBorderPosition(ugIndex: number, totalInversors: number): Position {
    const baseX = this.getUgStartX(ugIndex, totalInversors);
    const naturalWidth =
      this.getUgInversorsAmount(ugIndex) * this.INV_COLUMN_STEP;
    const extraWidth = Math.max(this.MIN_UG_BORDER_WIDTH - naturalWidth, 0);

    return { x: baseX - extraWidth / 2, y: 0 };
  }

  getUgBorderWidth(ugIndex: number): number {
    return Math.max(
      this.getUgInversorsAmount(ugIndex) * this.INV_COLUMN_STEP + 15,
      this.MIN_UG_BORDER_WIDTH,
    );
  }

  getSmartLoggerPosition(ugIndex: number, totalInversors: number): Position {
    return {
      x: this.getUgCenterX(ugIndex, totalInversors) - 12.5,
      y: 200,
    };
  }

  getSmartLoggerTitlePosition(
    ugIndex: number,
    totalInversors: number,
  ): Position {
    return {
      x: this.getUgCenterX(ugIndex, totalInversors) - 16,
      y: 210,
    };
  }

  getDJBTPosition(
    ugIndex: number,
    totalInversors: number,
    y: number,
  ): Position {
    return {
      x: this.getUgCenterX(ugIndex, totalInversors) - 12.5,
      y: y,
    };
  }

  getDJBTTitlePosition(
    ugIndex: number,
    totalInversors: number,
    y: number,
  ): Position {
    return {
      x: this.getUgCenterX(ugIndex, totalInversors) + 18,
      y: y - 2,
    };
  }

  getTrafoPosition(ugIndex: number, totalInversors: number): Position {
    return {
      x: this.getUgCenterX(ugIndex, totalInversors) - 15,
      y: 95,
    };
  }

  getTrafoTitlePosition(ugIndex: number, totalInversors: number): Position {
    return {
      x: this.getUgCenterX(ugIndex, totalInversors) + 20,
      y: 90,
    };
  }

  getDJMTPosition(ugIndex: number, totalInversors: number): Position {
    return {
      x: this.getUgCenterX(ugIndex, totalInversors) - 12.5,
      y: 40,
    };
  }

  getDJMTTitlePosition(ugIndex: number, totalInversors: number): Position {
    return {
      x: this.getUgCenterX(ugIndex, totalInversors) + 18,
      y: 42,
    };
  }

  getDJMTValuePosition(ugIndex: number, totalInversors: number): Position {
    return {
      x: this.getUgCenterX(ugIndex, totalInversors) + 20,
      y: 50,
    };
  }

  getInversorAndStringButtonPosition(
    ugIndex: number,
    inversorIndex: number,
    y: number,
  ): Position {
    const prevInversorsAmount = this.getPrevInversorsAmount(ugIndex);

    return {
      x:
        ugIndex * this.UG_GAP +
        (this.INV_COLUMN_STEP - 30) / 2 +
        inversorIndex * this.INV_COLUMN_STEP +
        prevInversorsAmount * this.INV_COLUMN_STEP,
      y: y,
    };
  }

  getReleProtecaoPosition(y: number) {
    return { x: this.getDiagramCenterX() - 15, y: y };
  }

  getReleProtecaoValuesPosition(y: number) {
    return { x: this.getDiagramCenterX() + 20, y: y };
  }

  // getReleProtecaoTitlesPosition(y: number) {
  //   const ugCount = Object.keys(this.UgInversorsCount).length;
  //   let totalInversors = 0;

  //   for (let i = 1; i < ugCount + 1; i++) {
  //     totalInversors += this.UgInversorsCount[i] || 0;
  //   }

  //   const x =
  //     Object.keys(this.UgInversorsCount).length > 1
  //       ? (ugCount * 23 + totalInversors * 32.6) / 2.25
  //       : totalInversors * 10.5;

  //   return { x: x, y: y };
  // }

  getSubstationBorderPosition() {
    return { x: this.getDiagramCenterX() - 60, y: -155 };
  }

  getSubstationTrafoPosition() {
    return { x: this.getDiagramCenterX() - 15, y: -65 };
  }

  getSubstationDisjuntorPosition(hasTransformer: boolean) {
    return {
      x: this.getDiagramCenterX() - 12.5,
      y: hasTransformer ? -110 : -85,
    };
  }
  getSubstationDisjuntorTitlePosition(hasTransformer: boolean) {
    return {
      x: this.getDiagramCenterX() + 18,
      y: hasTransformer ? -110 : -85,
    };
  }

  getSubstationTrafoTitlePosition() {
    return { x: this.getDiagramCenterX() + 18, y: -63 };
  }
}

export function getNumberFromString(input?: string): number {
  if (!input) return 0;
  const digits = input.replace(/\D+/g, "");
  return Number(digits);
}

export function getColorByUnavailabilityTime(
  unavailabilityTime: number,
): string {
  if (unavailabilityTime <= 3) return "#10b981";
  if (unavailabilityTime > 3 && unavailabilityTime <= 4) return "#f59e0b";
  if (unavailabilityTime > 4 && unavailabilityTime <= 5) return "#F97316";
  return "#ef4444";
}

export function getOperationalStatusByIdentifier(
  telemetryData: SolarGridControlTelemetryData,
  identifier: string,
): OperationalStatus {
  const componentStatus = telemetryData.componentOperationalStatuses.find(
    (status) => status.identifier === identifier,
  );
  const opStatus =
    componentStatus?.operationalStatus ?? OperationalStatus.Unknown;
  // console.debug("Operational status:", identifier, opStatus);
  return opStatus;
}

export function operationalStatusToStatus(
  operationalStatus: OperationalStatus,
) {
  switch (operationalStatus) {
    case OperationalStatus.On:
      return "on";
    case OperationalStatus.Off:
      return "off";
    case OperationalStatus.NoTelemetry:
      return "notelemetry";
    case OperationalStatus.Unknown:
    default:
      return "unknown";
  }
}
